/* ==========================================================================================
   Blenders Online 2026 · customer.js · the classic customer account pages
   (sections/main-login, main-register, main-account, main-addresses, main-order,
   main-activate-account, main-reset-password)

   Dawn 15's customer.js, rewritten. Dawn's version bound EVERY button[aria-expanded] on the page (the
   header's menus too) and needed global.js for the country list. This one touches only the account
   sections, needs nothing else, is safe to load twice, and re-binds when the theme editor redraws a section.
   The pages work without it: the forms are plain Shopify forms. It adds:

     1. Show / Hide on password boxes          [data-bo-pw] aria-controls="<input id> [<input id>]", its word in
                                               [data-bo-pw-word], data-show / data-hide; .is-on while shown
     2. Sign in <-> reset password panes       [data-bo-acct-panes]  (#recover / #login in the address)
     3. Focus on a message that needs reading  [data-bo-acct-focus]
     4. Address book                           [data-customer-addresses]
          open / close a form                  [data-bo-acct-toggle] aria-controls -> [data-bo-acct-panel]
          cancel                               [data-bo-acct-cancel] (a reset button)
          delete, asked twice                  form[data-bo-confirm] > [data-bo-confirm-ask] / -box / -yes / -no
          country -> state list                select[data-bo-country] data-bo-province data-bo-province-box
                                               data-default; data-bo-options-from="<id of the full list>"
     5. Print an order                         [data-bo-acct-print]  (hidden in the markup; shown here, since
                                               printing needs JavaScript; the print look is in bo-account.css)

   window.BOAccount.init(root) binds everything inside root. (window.CustomerAddresses is kept as a
   harmless stand-in for any Dawn markup that still calls `new CustomerAddresses()`.)
   ========================================================================================== */
(() => {
  'use strict';
  if (window.BOAccount) return;

  const W = window, D = document, H = D.documentElement;
  H.classList.add('bo-acct-js');

  const closest = (el, sel) => (el && el.closest ? el.closest(sel) : null);

  /* ---------- 1. Show / Hide password ---------- */
  const pwInputs = (btn) => (btn.getAttribute('aria-controls') || '')
    .split(/\s+/)
    .map((id) => (id ? D.getElementById(id) : null))
    .filter(Boolean);
  // The button's words say what a press will do ("Show password" / "Hide password": the second word is
  // there for screen readers only), so it carries no aria-pressed: "Hide, pressed" would say it twice.
  const setPw = (btn, show) => {
    pwInputs(btn).forEach((input) => { input.type = show ? 'text' : 'password'; });
    btn.classList.toggle('is-on', show);
    (btn.querySelector('[data-bo-pw-word]') || btn).textContent = show ? (btn.dataset.hide || 'Hide') : (btn.dataset.show || 'Show');
  };

  /* ---------- 2. Sign in <-> reset password ----------
     CSS shows the right pane from the address alone (#recover). This adds: a reset form that came back
     with an error gives way when "Back to sign in" is pressed, and the first box of the new pane gets focus. */
  const onHash = () => {
    const root = D.querySelector('[data-bo-acct-panes]');
    if (!root) return;
    const hash = W.location.hash;
    if (hash !== '#recover' && hash !== '#login') return;
    root.classList.toggle('is-recover', hash === '#recover');
    const pane = D.getElementById(hash.slice(1));
    const first = pane && pane.querySelector('input:not([type="hidden"])');
    if (first) first.focus({ preventScroll: true });
  };

  /* ---------- 4. Address book ---------- */
  const pick = (select, want) => {                   // choose an option by its value, else by its words
    if (!want) return false;
    for (let i = 0; i < select.options.length; i++) {
      const o = select.options[i];
      if (o.value === want || o.textContent === want) { select.selectedIndex = i; return true; }
    }
    return false;
  };
  const borrowOptions = (select) => {                // an edit form borrows the full country list, once
    const from = select.dataset.boOptionsFrom ? D.getElementById(select.dataset.boOptionsFrom) : null;
    if (!from || from === select || select.dataset.boFilled || !from.options.length) return;
    const frag = D.createDocumentFragment();
    for (let i = 0; i < from.options.length; i++) frag.appendChild(from.options[i].cloneNode(true));
    select.textContent = '';
    select.appendChild(frag);
    select.dataset.boFilled = '1';
  };
  const fillProvinces = (select, keepDefault) => {   // the states of the chosen country
    const province = D.getElementById(select.dataset.boProvince || '');
    if (!province) return;
    const box = D.getElementById(select.dataset.boProvinceBox || '');
    const option = select.options[select.selectedIndex];
    let list = [];
    try { list = JSON.parse((option && option.getAttribute('data-provinces')) || '[]') || []; } catch (e) { list = []; }
    const want = keepDefault ? province.getAttribute('data-default') : '';
    province.textContent = '';
    list.forEach((pair) => {
      const o = D.createElement('option');
      o.value = pair[0];
      o.textContent = pair[1];
      province.appendChild(o);
    });
    // no saved state to go back to: start on "Choose a state" (and insist on a choice), never on the first of the list
    const found = want ? pick(province, want) : false;
    const ask = province.getAttribute('data-bo-placeholder');
    if (!found && ask && list.length) {
      const o = D.createElement('option');
      o.value = '';
      o.textContent = ask;
      o.disabled = true;
      province.insertBefore(o, province.firstChild);
      province.selectedIndex = 0;
    }
    province.required = !!(ask && list.length);
    if (box) box.hidden = !list.length;
  };
  const bindCountry = (select) => {
    if (select.dataset.boBound) return;
    select.dataset.boBound = '1';
    const sync = () => {
      pick(select, select.getAttribute('data-default'));
      fillProvinces(select, true);
    };
    borrowOptions(select);
    sync();
    select.addEventListener('change', () => fillProvinces(select, false));
    if (select.form) select.form.addEventListener('reset', () => setTimeout(sync, 0));   // Cancel puts the saved address back
  };

  const setOpen = (button, open, focus) => {
    const panel = D.getElementById(button.getAttribute('aria-controls') || '');
    if (!panel) return;
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.classList.toggle('is-open', open);
    const box = closest(button, '[data-address]');
    if (box) box.classList.toggle('is-editing', open);
    if (!open) return;
    panel.querySelectorAll('select[data-bo-country]').forEach(bindCountry);
    if (focus) {                                     // the first box to TYPE in (the country above it is already chosen)
      const first = panel.querySelector('input:not([type="hidden"]):not([type="checkbox"])') || panel.querySelector('select');
      if (first) setTimeout(() => first.focus(), 60);
    }
  };
  const toggleOf = (panel) => (panel && panel.id ? D.querySelector('[data-bo-acct-toggle][aria-controls="' + panel.id + '"]') : null);

  const askDelete = (form, asking) => {
    const ask = form.querySelector('[data-bo-confirm-ask]');
    const box = form.querySelector('[data-bo-confirm-box]');
    if (!ask || !box) return false;
    ask.hidden = asking;
    box.hidden = !asking;
    const next = asking ? box.querySelector('[data-bo-confirm-no]') : ask;
    if (next) next.focus();
    return true;
  };

  /* ---------- one set of listeners for the whole page ---------- */
  D.addEventListener('pointerdown', (event) => {     // a mouse press on Show / Hide must not take the caret out of the box
    if (event.pointerType === 'mouse' && closest(event.target, '[data-bo-pw]')) event.preventDefault();
  });

  D.addEventListener('click', (event) => {
    const target = event.target;

    const pw = closest(target, '[data-bo-pw]');
    if (pw) { setPw(pw, !pw.classList.contains('is-on')); return; }

    if (closest(target, '[data-bo-acct-print]')) { W.print(); return; }

    const toggle = closest(target, '[data-bo-acct-toggle]');
    if (toggle) { setOpen(toggle, toggle.getAttribute('aria-expanded') !== 'true', true); return; }

    const cancel = closest(target, '[data-bo-acct-cancel]');
    if (cancel) {                                    // a reset button: the browser empties the form, this closes it
      const button = toggleOf(closest(cancel, '[data-bo-acct-panel]'));
      if (button) { setOpen(button, false); button.focus(); }
      return;
    }

    const yes = closest(target, '[data-bo-confirm-yes]');
    if (yes && yes.form) {                           // let this one submit through; the flag does not outlive the press
      const form = yes.form;
      form.dataset.boConfirmed = '1';
      setTimeout(() => { delete form.dataset.boConfirmed; }, 1500);
      return;
    }

    const no = closest(target, '[data-bo-confirm-no]');
    if (no) { const form = closest(no, 'form[data-bo-confirm]'); if (form) askDelete(form, false); }
  });

  D.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form || !form.matches) return;
    if (form.matches('form[data-bo-confirm]') && form.dataset.boConfirmed !== '1') {
      if (askDelete(form, true)) event.preventDefault();   // first press: ask. "Yes" sets the flag and the form goes
      return;
    }
    // a password shown as text goes back to dots before the form leaves (browsers then offer to save it)
    form.querySelectorAll('[data-bo-pw].is-on').forEach((btn) => setPw(btn, false));
  }, true);

  D.addEventListener('keydown', (event) => {         // Escape backs out of the delete question
    if (event.key !== 'Escape') return;
    const box = closest(event.target, '[data-bo-confirm-box]');
    const form = box ? closest(box, 'form[data-bo-confirm]') : null;
    if (form) askDelete(form, false);
  });

  W.addEventListener('hashchange', onHash);

  /* ---------- bind what is on the page ---------- */
  const init = (root) => {
    const scope = root && root.querySelectorAll ? root : D;
    scope.querySelectorAll('[data-bo-pw], [data-bo-acct-print]').forEach((btn) => { btn.hidden = false; });
    scope.querySelectorAll('[data-customer-addresses]').forEach((book) => {
      // the full country list (the new-address form) first, then any form that is already open
      book.querySelectorAll('select[data-bo-country]:not([data-bo-options-from])').forEach(bindCountry);
      book.querySelectorAll('[data-bo-acct-panel].is-open select[data-bo-country]').forEach(bindCountry);
    });
    const message = scope.querySelector('[data-bo-acct-focus]');
    if (message && (D.activeElement === D.body || !D.activeElement)) message.focus();
  };

  W.BOAccount = { init };
  W.CustomerAddresses = W.CustomerAddresses || function CustomerAddresses() { init(D); };

  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D));
  else init(D);
  D.addEventListener('shopify:section:load', (event) => init(event.target));
})();
